import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

// Cloudflare Turnstile: proves a person asked for an email code, so a script
// cannot have codes sent to a list of strangers. Usually invisible
// ("interaction-only"): Cloudflare shows a checkbox only when it is unsure.
//
// The site key is public by design (Cloudflare sends it to every visitor), so
// it lives here rather than in a build variable that can silently stay empty.
// Only the secret is private: TURNSTILE_SECRET_KEY on the API, which checks
// tokens only once that secret is set. The widget answers only on hostnames
// registered for it in Cloudflare, so staging, dev and localhost get none.
const PRODUCTION_SITE_KEY = '0x4AAAAAAC77tJvr_mjb6MT2';
const PRODUCTION_HOSTS = ['app.serviti.ai'];
export const siteKeyFor = (hostname) => (PRODUCTION_HOSTS.includes(hostname) ? PRODUCTION_SITE_KEY : '');
// REACT_APP_TURNSTILE_SITE_KEY overrides, for tests and local trials only.
const siteKey = () => process.env.REACT_APP_TURNSTILE_SITE_KEY || siteKeyFor(window.location.hostname);
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const turnstileEnabled = () => Boolean(siteKey());

let scriptPromise = null;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile missing')));
      script.onerror = () => {
        scriptPromise = null; // let a later render try again
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Renders the check and reports its token through onToken ('' when there is
 * none yet, it expired, or it errored). A token is single-use: call
 * ref.current.reset() after each request to get a fresh one.
 */
const TurnstileWidget = forwardRef(({ action, onToken }, ref) => {
  const container = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  const [loadFailed, setLoadFailed] = useState(false);
  onTokenRef.current = onToken;

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current('');
      if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
    },
  }), []);

  useEffect(() => {
    const key = siteKey();
    if (!key) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId.current = turnstile.render(container.current, {
          sitekey: key,
          action,
          appearance: 'interaction-only',
          callback: (token) => onTokenRef.current(token || ''),
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
        });
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current !== null) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [action]);

  if (!siteKey()) return null;
  return (
    <div>
      <div ref={container} data-testid="turnstile-container" />
      {loadFailed && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          The security check could not load. Check your connection or turn off content blockers for this page,
          then refresh. Still stuck? Message us on WhatsApp at +91 85558 11800.
        </p>
      )}
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
